// Display helpers shared between the Credit Notes list and detail summary.
// Single source of truth for the EU-SEPA / non-SEPA labelling and for money
// formatting so the two views can't drift apart.

import { CcnPaymentMethod, CcnSepaType } from '@/enums/CcnPaymentMethod'
import { BASE_CURRENCY } from '@/enums/Currency'

export const formatAmount = (
  raw: string | number | null | undefined,
  currency: string = BASE_CURRENCY
): string => {
  if (raw === null || raw === undefined || raw === '') return '—'
  const value = Number(raw)
  if (Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value)
}

type CcnAmounts = Pick<
  CcnListItem,
  | 'fee'
  | 'valueAddedTax'
  | 'finalFee'
  | 'payoutCurrency'
  | 'payoutFee'
  | 'payoutValueAddedTax'
  | 'payoutFinalFee'
  | 'exchangeRate'
  | 'exchangeRateDate'
  | 'exchangeRateSource'
  | 'fixedFeePerOrder'
>

export const isForeignCurrencyCcn = (ccn: Pick<CcnAmounts, 'payoutCurrency'>): boolean =>
  !!ccn.payoutCurrency && ccn.payoutCurrency !== BASE_CURRENCY

const payoutField = {
  fee: 'payoutFee',
  valueAddedTax: 'payoutValueAddedTax',
  finalFee: 'payoutFinalFee'
} as const

// The amount as the linguist sees it: payout currency when the CCN has one, EUR otherwise.
export const formatCcnAmount = (
  ccn: CcnAmounts,
  field: 'fee' | 'valueAddedTax' | 'finalFee'
): string =>
  isForeignCurrencyCcn(ccn)
    ? formatAmount(ccn[payoutField[field]], ccn.payoutCurrency ?? BASE_CURRENCY)
    : formatAmount(ccn[field])

const rateSourceLabel: Record<string, string> = {
  ecb: 'ECB reference rate',
  linguist: 'fixed rate of the linguist',
  manual: 'entered manually'
}

export const formatExchangeRate = (ccn: CcnAmounts): string => {
  if (!isForeignCurrencyCcn(ccn) || !ccn.exchangeRate) return '—'
  const rate = Number(ccn.exchangeRate)
  const formattedRate = Number.isNaN(rate)
    ? ccn.exchangeRate
    : new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(
        rate
      )
  const details = [
    ccn.exchangeRateSource
      ? rateSourceLabel[ccn.exchangeRateSource] ?? ccn.exchangeRateSource
      : null,
    ccn.exchangeRateDate ? new Date(ccn.exchangeRateDate).toLocaleDateString('de-DE') : null
  ].filter(Boolean)
  return `1 EUR = ${formattedRate} ${ccn.payoutCurrency}${details.length ? ` (${details.join(', ')})` : ''}`
}

export const formatPaymentMethod = (ccn: {
  paymentMethod?: string | null
  sepaType?: string | null
}): string => {
  if (!ccn.paymentMethod) return '—'
  if (ccn.paymentMethod === CcnPaymentMethod.BANK_TRANSFER) {
    return ccn.sepaType === CcnSepaType.EU_SEPA
      ? 'Bank transfer (EU SEPA)'
      : 'Bank transfer (non SEPA)'
  }
  return ccn.paymentMethod
}
